import type { LearningExercise } from "../types";

// Hagen BGB-AT, Schuldrecht Allgemeiner Teil §§ 241–432.
// Abdeckung: Schuldverhältnis & Pflichten (§§ 241, 242, 311 II/III, 241a),
// Leistungsstörungen (§§ 275, 280 I/II/III, 281, 282, 283, 286, 288, 311a II, 284),
// Rücktritt & Gefahrtragung (§§ 323, 325, 326, 346 ff.),
// Schadensrecht (§§ 249, 251, 252, 253, 254),
// Erlöschen (§§ 362, 387 ff., 397),
// Mehrheiten (§§ 421, 426),
// Abtretung (§§ 398, 404, 407).
// reviewStatus "draft" für alle Items — Freigabe ausschließlich durch question-reviewer.
export const hagenSchuldrechtAt: LearningExercise[] = [

  // ─── MC SINGLE hj-schuld-001 … hj-schuld-014 ─────────────────────────────

  {
    id: "hj-schuld-001",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Schuldverhältnis & Pflichten",
    title: "Leistungs- vs. Rücksichtnahmepflichten (§ 241 BGB)",
    prompt:
      "Welche Aussage beschreibt den Unterschied zwischen der Leistungspflicht nach § 241 Abs. 1 BGB und der Rücksichtnahmepflicht nach § 241 Abs. 2 BGB korrekt?",
    options: [
      "Beide Pflichten bestehen nur aus einem Schuldverhältnis mit vertraglicher Grundlage; außervertragliche Schuldverhältnisse erzeugen nur Leistungspflichten.",
      "§ 241 Abs. 1 BGB berechtigt den Gläubiger zur Leistung; § 241 Abs. 2 BGB verpflichtet beide Parteien zur Rücksicht auf Rechte, Rechtsgüter und Interessen des anderen Teils.",
      "Die Rücksichtnahmepflicht nach § 241 Abs. 2 BGB ersetzt bei ihrer Verletzung stets die Leistungspflicht.",
      "§ 241 Abs. 2 BGB begründet eigenständige Ansprüche auf ein bestimmtes Handeln, nicht lediglich Schutzpflichten.",
    ],
    answers: [1],
    explanation:
      "§ 241 Abs. 1 BGB: Kraft des Schuldverhältnisses ist der Gläubiger berechtigt, von dem Schuldner eine Leistung zu fordern; diese kann auch in einem Unterlassen bestehen. § 241 Abs. 2 BGB: Das Schuldverhältnis kann nach seinem Inhalt jeden Teil zur Rücksicht auf die Rechte, Rechtsgüter und Interessen des anderen Teils verpflichten. Rücksichtnahmepflichten erzeugen keine Hauptleistungsansprüche, sondern Schutzpflichten, deren Verletzung Schadensersatz nach § 280 Abs. 1 BGB auslösen kann. Sie können auch ohne Hauptleistungspflicht bestehen (vgl. § 311 Abs. 2, 3 BGB).",
    sourceIds: ["bgb241"],
    sources: [
      { id: "bgb241", label: "§ 241 BGB", url: "https://www.gesetze-im-internet.de/bgb/__241.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "schuldverhaeltnis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-002",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Treu und Glauben",
    title: "Treu und Glauben (§ 242 BGB) — Anwendungsbereich",
    prompt:
      "A schuldet B Zahlung von 1.000 €. B verlangt bewusst übertrieben schnelle Zahlung innerhalb von 24 Stunden, um A unter Druck zu setzen. Welche Norm schränkt die Rechtsausübung des B am direktesten ein?",
    options: [
      "§ 226 BGB (Schikaneverbot), weil B ausschließlich zum Schaden von A handelt.",
      "§ 242 BGB (Treu und Glauben), weil die Rechtsausübung nach den konkreten Umständen gegen das Gebot von Treu und Glauben verstößt.",
      "§ 138 BGB (Sittenwidrigkeit), weil B sittenwidrig handelt.",
      "§ 313 BGB (Störung der Geschäftsgrundlage), weil die ursprüngliche Zahlungsabrede gestört ist.",
    ],
    answers: [1],
    explanation:
      "§ 242 BGB: Der Schuldner ist verpflichtet, die Leistung so zu bewirken, wie Treu und Glauben mit Rücksicht auf die Verkehrssitte es erfordern. § 242 BGB wirkt als allgemeine Schranke der Rechtsausübung ('dolo agit'-Einwand, Verwirkung, unzulässige Rechtsausübung). § 226 BGB (Schikane) greift nur bei allein schädigender Absicht ohne eigenes rechtliches Interesse. § 242 BGB ist der primäre Maßstab für missbräuchliche, aber nicht rein schikanöse Rechtsausübung.",
    sourceIds: ["bgb242"],
    sources: [
      { id: "bgb242", label: "§ 242 BGB", url: "https://www.gesetze-im-internet.de/bgb/__242.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["schuldrecht-at", "treu-und-glauben"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-003",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - c.i.c.",
    title: "Vorvertragliches Schuldverhältnis (§ 311 Abs. 2 BGB)",
    prompt:
      "G betritt das Ladenlokal des V, um sich über Produkte zu informieren. Beim Betreten rutscht G auf dem frisch gewischten Boden aus und verletzt sich. Auf welcher Grundlage kommt ein Schadensersatzanspruch des G gegen V in Betracht?",
    options: [
      "§ 280 Abs. 1 i.V.m. § 311 Abs. 2 Nr. 2 BGB (vorvertragliches Schuldverhältnis durch Kontakt), weil G durch Betreten des Geschäfts in den Einflussbereich von V gelangt ist.",
      "§ 823 Abs. 1 BGB (unerlaubte Handlung) ist die einzige Grundlage, da kein Vertrag besteht.",
      "§ 280 Abs. 1 i.V.m. § 433 BGB, weil durch das Betreten des Ladens bereits ein Kaufvertrag entstanden ist.",
      "§ 311 Abs. 3 BGB (culpa in contrahendo durch Dritten), weil es auf den konkreten Verhandlungsführer ankommt.",
    ],
    answers: [0],
    explanation:
      "§ 311 Abs. 2 Nr. 2 BGB: Ein Schuldverhältnis mit Rücksichtnahmepflichten entsteht durch die Anbahnung eines Vertrags, bei welcher der eine Teil im Hinblick auf eine etwaige rechtsgeschäftliche Beziehung dem anderen Teil die Möglichkeit zur Einwirkung auf seine Rechte, Rechtsgüter und Interessen gewährt oder ihm diese anvertraut (Geschäftskontakt). Das klassische Beispiel 'Salatblatt-Fall' (BGH) zeigt: Das Betreten eines Ladens zum Einkauf begründet ein vorvertragliches SV nach § 311 Abs. 2 Nr. 2 BGB. Schutzpflichten aus § 241 Abs. 2 BGB sind verletzt; Haftung nach § 280 Abs. 1 BGB.",
    sourceIds: ["bgb311", "bgb241", "bgb280"],
    sources: [
      { id: "bgb311", label: "§ 311 BGB", url: "https://www.gesetze-im-internet.de/bgb/__311.html" },
      { id: "bgb241", label: "§ 241 BGB", url: "https://www.gesetze-im-internet.de/bgb/__241.html" },
      { id: "bgb280", label: "§ 280 BGB", url: "https://www.gesetze-im-internet.de/bgb/__280.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["schuldrecht-at", "cic", "leistungsstoerungen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-004",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Unmöglichkeit",
    title: "Unmöglichkeit — Abgrenzung der Fallgruppen (§ 275 BGB)",
    prompt:
      "Welche Fallgruppe der Unmöglichkeit liegt vor, wenn ein Sänger für ein Konzert verpflichtet ist und kurz vorher erkrankt, sodass die Leistung nur durch ihn persönlich erbracht werden kann?",
    options: [
      "Rechtliche Unmöglichkeit nach § 275 Abs. 1 BGB, weil der Vertrag wegen Krankheit nichtig ist.",
      "Faktische Unmöglichkeit nach § 275 Abs. 2 BGB, weil der Aufwand der Leistungserbringung in keinem Verhältnis zum Gläubigerinteresse steht.",
      "Persönliche Unmöglichkeit nach § 275 Abs. 3 BGB, weil die Leistung höchstpersönlich ist und dem Schuldner unzumutbar ist.",
      "Anfängliche Unmöglichkeit nach § 311a Abs. 1 BGB, weil die Unmöglichkeit schon bei Vertragsschluss vorlag.",
    ],
    answers: [2],
    explanation:
      "§ 275 Abs. 3 BGB: Der Schuldner kann die Leistung verweigern, wenn er die Leistung persönlich zu erbringen hat und sie ihm unter Abwägung des seiner Leistung entgegenstehenden Hindernisses mit dem Leistungsinteresse des Gläubigers nicht zugemutet werden kann. Diese persönliche Unmöglichkeit (auch 'Unzumutbarkeit bei höchstpersönlichen Leistungen' genannt) ist der einschlägige Tatbestand. § 275 Abs. 1 BGB erfasst objektive/echte Unmöglichkeit (niemand kann leisten); § 275 Abs. 2 BGB die faktische Unmöglichkeit (unverhältnismäßiger Aufwand, kein Personenbezug nötig); § 311a BGB regelt die anfängliche Unmöglichkeit bei Vertragsschluss.",
    sourceIds: ["bgb275"],
    sources: [
      { id: "bgb275", label: "§ 275 BGB", url: "https://www.gesetze-im-internet.de/bgb/__275.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "unmoeglichkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-005",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Pflichtverletzung",
    title: "Grundtatbestand Schadensersatz (§ 280 Abs. 1 BGB)",
    prompt:
      "Welche drei kumulativen Voraussetzungen müssen für einen Schadensersatzanspruch nach § 280 Abs. 1 BGB vorliegen?",
    options: [
      "Vertragsverhältnis, Mahnung des Schuldners und Verschulden.",
      "Schuldverhältnis, Pflichtverletzung und Schaden — wobei Vertretenmüssen nach § 280 Abs. 1 S. 2 BGB vermutet wird.",
      "Schuldverhältnis, Pflichtverletzung und positives Verschulden des Schuldners, das der Gläubiger beweist.",
      "Nichterfüllung, Fristsetzung und Mahnung gemäß § 286 BGB.",
    ],
    answers: [1],
    explanation:
      "§ 280 Abs. 1 S. 1 BGB: Verletzt der Schuldner eine Pflicht aus dem Schuldverhältnis, kann der Gläubiger Ersatz des hierdurch entstehenden Schadens verlangen. § 280 Abs. 1 S. 2 BGB: Dies gilt nicht, wenn der Schuldner die Pflichtverletzung nicht zu vertreten hat — das Vertretenmüssen wird also vermutet (Beweislastumkehr). Mahnung ist kein Tatbestandsmerkmal des § 280 Abs. 1 BGB; sie ist erst für den Verzögerungsschaden nach § 280 Abs. 2 i.V.m. § 286 BGB erforderlich.",
    sourceIds: ["bgb280"],
    sources: [
      { id: "bgb280", label: "§ 280 BGB", url: "https://www.gesetze-im-internet.de/bgb/__280.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "pflichtverletzung", "leistungsstoerungen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-006",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Verzug",
    title: "Mahnung und Verzugseintritt (§ 286 BGB) — Fallkonstellation",
    prompt:
      "S schuldet G 500 € aus Kaufvertrag, fällig am 1. März. G mahnt am 5. März. S zahlt nicht. Welche Aussage zum Verzug ist korrekt?",
    options: [
      "Verzug tritt automatisch mit Fälligkeit am 1. März ein, da keine Mahnung nötig ist.",
      "Verzug tritt erst ein, wenn G nach erfolgloser Mahnung Klage erhebt.",
      "Verzug tritt mit Zugang der Mahnung am 5. März ein, sofern S die Nicht-Zahlung zu vertreten hat (§ 286 Abs. 1 i.V.m. § 286 Abs. 4 BGB).",
      "Verzug setzt eine notarielle Mahnung voraus, da § 286 BGB Formvorschriften kennt.",
    ],
    answers: [2],
    explanation:
      "§ 286 Abs. 1 S. 1 BGB: Leistet der Schuldner auf eine Mahnung des Gläubigers nicht, die nach dem Eintritt der Fälligkeit erfolgt, so kommt er durch die Mahnung in Verzug. § 286 Abs. 4 BGB: Der Schuldner kommt nicht in Verzug, solange die Leistung infolge eines Umstands unterbleibt, den er nicht zu vertreten hat. Die Mahnung ist formfrei (§ 286 Abs. 1 BGB); eine notarielle Form ist gesetzlich nicht vorgesehen. Ausnahmen der Mahnungserforderlichkeit regelt § 286 Abs. 2 BGB (z.B. Kalenderdatum, Verweigerung).",
    sourceIds: ["bgb286"],
    sources: [
      { id: "bgb286", label: "§ 286 BGB", url: "https://www.gesetze-im-internet.de/bgb/__286.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Verzug",
    title: "Entbehrlichkeit der Mahnung (§ 286 Abs. 2 BGB)",
    prompt:
      "In welchem der folgenden Fälle bedarf es keiner Mahnung nach § 286 Abs. 2 BGB, damit der Schuldner in Verzug gerät?",
    options: [
      "Der Gläubiger hat dem Schuldner mündlich mitgeteilt, dass er auf Zahlung wartet.",
      "Der Schuldner hat die Leistung trotz Fälligkeit einmal übersehen.",
      "Für die Leistung ist nach dem Kalender ein bestimmter Zeitpunkt vereinbart (§ 286 Abs. 2 Nr. 1 BGB).",
      "Der Schuldner ist unbekannt verzogen und daher nicht erreichbar.",
    ],
    answers: [2],
    explanation:
      "§ 286 Abs. 2 Nr. 1 BGB: Es bedarf keiner Mahnung, wenn für die Leistung eine Zeit nach dem Kalender bestimmt ist. Weitere Fälle: Nr. 2 (Berechnung nach einem Ereignis innerhalb angemessener Frist), Nr. 3 (ernsthafte und endgültige Erfüllungsverweigerung durch den Schuldner), Nr. 4 (besondere Gründe, die sofortigen Verzug rechtfertigen). Eine bloße mündliche Aufforderung ist eine Mahnung (kein § 286 Abs. 2 Nr. 1-Fall). Unerreichbarkeit des Schuldners ist kein gesetzlich kodifizierter Ausnahmetatbestand des § 286 Abs. 2 BGB.",
    sourceIds: ["bgb286"],
    sources: [
      { id: "bgb286", label: "§ 286 BGB", url: "https://www.gesetze-im-internet.de/bgb/__286.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-008",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Schadensersatz statt der Leistung",
    title: "Fristsetzung für SchadE statt der Leistung (§ 281 BGB) — Fallkonstellation",
    prompt:
      "K hat bei V eine Maschine bestellt. V liefert nicht. K setzt eine angemessene Nachfrist von zwei Wochen. V liefert auch dann nicht. Welche Rechte hat K?",
    options: [
      "K kann sofort Schadensersatz statt der Leistung nach § 280 Abs. 1 BGB verlangen, ohne weitere Voraussetzungen.",
      "K kann nach fruchtlosem Fristablauf Schadensersatz statt der Leistung nach §§ 280 Abs. 1, Abs. 3, 281 Abs. 1 BGB verlangen und gleichzeitig vom Vertrag zurücktreten (§§ 323, 325 BGB).",
      "K kann nur zurücktreten (§ 323 BGB); Schadensersatz ist bei Lieferverzögerungen ausgeschlossen.",
      "K muss nach § 283 BGB vorgehen, da V unmöglich leistet.",
    ],
    answers: [1],
    explanation:
      "§ 281 Abs. 1 S. 1 BGB: Soweit der Schuldner die fällige Leistung nicht oder nicht wie geschuldet erbringt, kann der Gläubiger nach Ablauf einer von ihm gesetzten angemessenen Nachfrist Schadensersatz statt der Leistung verlangen. Tatbestand: (1) Pflichtverletzung (§ 280 Abs. 1 BGB), (2) Fristsetzung nach § 281 Abs. 1 BGB, (3) fruchtloser Fristablauf, (4) Vertretenmüssen. § 325 BGB: Der Rücktritt schließt Schadensersatz nicht aus; K kann beides nebeneinander geltend machen. § 283 BGB gilt für nachträgliche objektive Unmöglichkeit, nicht für bloße Lieferverzögerung.",
    sourceIds: ["bgb280", "bgb281", "bgb325"],
    sources: [
      { id: "bgb280", label: "§ 280 BGB", url: "https://www.gesetze-im-internet.de/bgb/__280.html" },
      { id: "bgb281", label: "§ 281 BGB", url: "https://www.gesetze-im-internet.de/bgb/__281.html" },
      { id: "bgb325", label: "§ 325 BGB", url: "https://www.gesetze-im-internet.de/bgb/__325.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "leistungsstoerungen", "schadensersatz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-009",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Naturalrestitution und Geldersatz (§§ 249, 251 BGB)",
    prompt:
      "X beschädigt das Auto des Y durch fahrlässiges Auffahren. Y verlangt Schadensersatz. Was ist nach §§ 249 ff. BGB der vorrangige Anspruchsinhalt?",
    options: [
      "Sofortiger Geldersatz nach § 251 Abs. 1 BGB, weil Reparatur immer unverhältnismäßig ist.",
      "Naturalrestitution nach § 249 Abs. 1 BGB (Herstellung des Zustands vor dem schädigenden Ereignis); bei Sachbeschädigung alternativ Geldentschädigung nach § 249 Abs. 2 S. 1 BGB.",
      "Nur Wertersatz nach § 251 BGB, da das Fahrzeug eine Sache ist.",
      "Nichts, da § 249 BGB nur für vertragliche Schuldverhältnisse gilt.",
    ],
    answers: [1],
    explanation:
      "§ 249 Abs. 1 BGB: Wer zum Schadensersatz verpflichtet ist, hat den Zustand herzustellen, der bestehen würde, wenn der zum Ersatz verpflichtende Umstand nicht eingetreten wäre (Naturalrestitution als Grundsatz). § 249 Abs. 2 S. 1 BGB: Ist wegen Verletzung einer Person oder wegen Beschädigung einer Sache Schadensersatz zu leisten, kann der Gläubiger statt der Herstellung den dazu erforderlichen Geldbetrag verlangen. § 251 BGB (Geldersatz) greift nur, wenn Herstellung nicht möglich oder nicht genügend ist. § 249 BGB gilt für alle Schadensersatzansprüche, vertragliche und deliktische.",
    sourceIds: ["bgb249", "bgb251"],
    sources: [
      { id: "bgb249", label: "§ 249 BGB", url: "https://www.gesetze-im-internet.de/bgb/__249.html" },
      { id: "bgb251", label: "§ 251 BGB", url: "https://www.gesetze-im-internet.de/bgb/__251.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "schadensrecht"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-010",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Mitverschulden (§ 254 BGB) — Fallkonstellation",
    prompt:
      "A fährt ohne Sicherheitsgurt Auto und wird von B schuldhaft angefahren. A verletzt sich am Kopf. Wie wirkt sich der fehlende Gurt aus?",
    options: [
      "A hat keinen Schadensersatzanspruch, weil sein eigenes Verschulden den Anspruch vollständig ausschließt.",
      "Der Anspruch des A wird nach § 254 Abs. 1 BGB wegen mitwirkenden Verschuldens anteilig gemindert, wenn das Unterlassen des Gurts zum Schadensumfang beigetragen hat.",
      "B haftet voll, weil er die alleinige Unfallursache gesetzt hat.",
      "§ 254 BGB gilt nur bei Vertragsparteien, nicht bei Deliktsansprüchen.",
    ],
    answers: [1],
    explanation:
      "§ 254 Abs. 1 BGB: Hat bei der Entstehung des Schadens ein Verschulden des Beschädigten mitgewirkt, hängt die Verpflichtung zum Ersatz sowie der Umfang des zu leistenden Ersatzes von den Umständen, insbesondere davon ab, inwieweit der Schaden vorwiegend von dem einen oder dem anderen Teil verursacht worden ist. Unterlassen des Sicherheitsgurts ist anerkanntermaßen mitwirkendes Verschulden, das die Kopfverletzung mitverursacht. Der Anspruch erlischt nicht vollständig, sondern wird quotal gemindert. § 254 BGB gilt nach seinem Wortlaut für alle Schadensersatzansprüche (vertraglich und deliktisch).",
    sourceIds: ["bgb254"],
    sources: [
      { id: "bgb254", label: "§ 254 BGB", url: "https://www.gesetze-im-internet.de/bgb/__254.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["schuldrecht-at", "schadensrecht", "mitverschulden"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-011",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Rücktritt",
    title: "Rücktritt nach Fristsetzung (§ 323 BGB) — Verhältnis zu § 325 BGB",
    prompt:
      "Nach fruchtlosem Ablauf der Nachfrist (§ 323 Abs. 1 BGB) erklärt K den Rücktritt. Kann K gleichzeitig Schadensersatz verlangen?",
    options: [
      "Nein, Rücktritt und Schadensersatz schließen sich gegenseitig aus.",
      "Nur wenn V zusätzlich grob fahrlässig gehandelt hat.",
      "Ja, nach § 325 BGB lässt das Rücktrittsrecht den Schadensersatzanspruch unberührt; beides ist nebeneinander möglich.",
      "Ja, aber nur der Rücktritt muss zuerst vollständig abgewickelt sein.",
    ],
    answers: [2],
    explanation:
      "§ 325 BGB: Das Recht, bei einem gegenseitigen Vertrag Schadensersatz zu verlangen, wird durch den Rücktritt nicht ausgeschlossen. Diese Norm stellt klar, dass Rücktritt (§§ 323 ff. BGB) und Schadensersatz statt der Leistung (§§ 280 Abs. 3, 281 BGB) nebeneinander bestehen können. Der Gläubiger muss nicht wählen; er kann zurücktreten und zugleich den 'großen' Schadensersatz verlangen. Die frühere Ausschlussregel (§ 325 Abs. 1 a.F.) ist mit der Schuldrechtsreform 2002 weggefallen.",
    sourceIds: ["bgb323", "bgb325"],
    sources: [
      { id: "bgb323", label: "§ 323 BGB", url: "https://www.gesetze-im-internet.de/bgb/__323.html" },
      { id: "bgb325", label: "§ 325 BGB", url: "https://www.gesetze-im-internet.de/bgb/__325.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["schuldrecht-at", "ruecktritt"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-012",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Erlöschen",
    title: "Erfüllung (§ 362 BGB) — Leistung an Erfüllungs statt vs. erfüllungshalber",
    prompt:
      "G hat gegen S eine Geldforderung. S übergibt G ein gebrauchtes Fahrrad und erklärt, dies solle die Schuld tilgen. Was bestimmt § 364 BGB?",
    options: [
      "Das Schuldverhältnis erlischt immer, sobald der Schuldner irgendeinen Gegenstand übergibt.",
      "Nur wenn G die Annahme des Fahrrads als Leistung an Erfüllungs statt (§ 364 Abs. 1 BGB) akzeptiert, erlischt die Forderung; sonst bleibt sie bestehen.",
      "§ 362 BGB ordnet an, dass das Schuldverhältnis mit jeder Sachleistung erlischt.",
      "S begeht durch die Überreichung des Fahrrads eine Pflichtverletzung, die § 280 BGB auslöst.",
    ],
    answers: [1],
    explanation:
      "§ 362 Abs. 1 BGB: Das Schuldverhältnis erlischt, wenn die geschuldete Leistung an den Gläubiger bewirkt wird. § 364 Abs. 1 BGB: Das Schuldverhältnis erlischt, wenn der Gläubiger eine andere als die geschuldete Leistung an Erfüllungs statt annimmt. Die Annahme durch den Gläubiger ist konstitutiv; ohne seine Zustimmung kann S nicht durch Sachleistung erfüllen. 'Erfüllungshalber' (§ 364 Abs. 2 BGB) meint dagegen, dass die alte Forderung erst erlischt, wenn die neue Leistung (z.B. Scheck) tatsächlich erfüllt wird.",
    sourceIds: ["bgb362", "bgb364"],
    sources: [
      { id: "bgb362", label: "§ 362 BGB", url: "https://www.gesetze-im-internet.de/bgb/__362.html" },
      { id: "bgb364", label: "§ 364 BGB", url: "https://www.gesetze-im-internet.de/bgb/__364.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "erlöschen", "erfuellung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-013",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Aufrechnung",
    title: "Aufrechnungsvoraussetzungen (§§ 387, 388 BGB)",
    prompt:
      "Welche Voraussetzungen müssen für eine wirksame Aufrechnung nach § 387 BGB kumulativ vorliegen?",
    options: [
      "Gleichartigkeit der Leistungen, Fälligkeit der Gegenforderung, Erfüllbarkeit der Hauptforderung, Gegenseitigkeit — und Aufrechnungserklärung nach § 388 BGB.",
      "Nur Gleichartigkeit und Fälligkeit beider Forderungen; eine Erklärung ist entbehrlich.",
      "Schriftliche Aufrechnungserklärung, notarielle Beurkundung bei Forderungen über 5.000 €.",
      "Übereinstimmung beider Forderungen in Höhe und Fälligkeit; Überschuss wird nicht ausgeglichen.",
    ],
    answers: [0],
    explanation:
      "§ 387 BGB: Wenn zwei Personen einander Leistungen schulden, die ihrem Gegenstand nach gleichartig sind, kann jeder Teil seine Forderung gegen die Forderung des anderen Teils aufrechnen, sobald er die ihm gebührende Leistung fordern und die ihm obliegende Leistung bewirken kann. Voraussetzungen: (1) Gegenseitigkeit (Gläubiger der Hauptforderung = Schuldner der Gegenforderung), (2) Gleichartigkeit (typischerweise Geldforderungen), (3) Fälligkeit der Gegenforderung, (4) Erfüllbarkeit der Hauptforderung. § 388 S. 1 BGB: Die Aufrechnung erfolgt durch Erklärung gegenüber dem anderen Teil. Formfreiheit. Schriftliche oder notarielle Form ist nicht gesetzlich vorgesehen.",
    sourceIds: ["bgb387", "bgb388"],
    sources: [
      { id: "bgb387", label: "§ 387 BGB", url: "https://www.gesetze-im-internet.de/bgb/__387.html" },
      { id: "bgb388", label: "§ 388 BGB", url: "https://www.gesetze-im-internet.de/bgb/__388.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "erlöschen", "aufrechnung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-014",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "single",
    topic: "Schuldrecht AT - Gesamtschuld",
    title: "Gesamtschuld und Innenausgleich (§§ 421, 426 BGB)",
    prompt:
      "A und B schulden G gesamtschuldnerisch 6.000 €. G verlangt den vollen Betrag von A. A zahlt. Was folgt daraus im Verhältnis A zu B?",
    options: [
      "A kann von B nichts verlangen, weil er freiwillig gezahlt hat.",
      "A hat gegen B einen Ausgleichsanspruch nach § 426 Abs. 1 BGB in Höhe des Anteils, der auf B entfällt — mangels anderweitiger Vereinbarung je zur Hälfte.",
      "A tritt kraft Gesetzes in den Anspruch des G gegen B ein, ohne einen eigenen Anspruch nach § 426 BGB zu haben.",
      "G kann den vollen Betrag von 6.000 € zusätzlich auch noch von B verlangen, weil jeder Gesamtschuldner stets auf das Ganze haftet.",
    ],
    answers: [1],
    explanation:
      "§ 421 S. 1 BGB: Schulden mehrere eine Leistung in der Weise, dass jeder die ganze Leistung zu bewirken verpflichtet, der Gläubiger aber die Leistung nur einmal zu fordern berechtigt ist (Gesamtschuldner), so kann der Gläubiger die Leistung nach seinem Belieben von jedem der Schuldner ganz oder zu einem Teil fordern. § 426 Abs. 1 S. 1 BGB: Die Gesamtschuldner sind im Verhältnis zueinander zu gleichen Anteilen verpflichtet, soweit nicht ein anderes bestimmt ist. A hat damit einen Ausgleichsanspruch gegen B in Höhe von 3.000 €. Zusätzlich geht nach § 426 Abs. 2 BGB die Forderung des G gegen B kraft Gesetzes auf A über, soweit A über seinen Anteil hinaus geleistet hat (cessio legis). Im Außenverhältnis erlischt die Gesamtschuld dagegen mit der vollständigen Zahlung durch A für alle Schuldner (§ 422 Abs. 1 BGB) — G kann den Betrag nicht ein zweites Mal von B verlangen.",
    sourceIds: ["bgb421", "bgb426", "bgb422"],
    sources: [
      { id: "bgb421", label: "§ 421 BGB", url: "https://www.gesetze-im-internet.de/bgb/__421.html" },
      { id: "bgb426", label: "§ 426 BGB", url: "https://www.gesetze-im-internet.de/bgb/__426.html" },
      { id: "bgb422", label: "§ 422 BGB", url: "https://www.gesetze-im-internet.de/bgb/__422.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["schuldrecht-at", "mehrheiten", "gesamtschuld"],
    // reviewed (Re-Gate final 2026-06-03): Distraktor [3]/§ 422 bestätigt.
    reviewStatus: "beta-ready",
  },

  // ─── MC MULTI hj-schuld-015 … hj-schuld-021 ──────────────────────────────

  {
    id: "hj-schuld-015",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Unmöglichkeit",
    title: "Tatbestände des § 275 BGB — Mehrfachauswahl",
    prompt:
      "Welche der folgenden Konstellationen sind im BGB als eigenständige Tatbestände des § 275 BGB kodifiziert? (Mehrere Antworten möglich)",
    options: [
      "Objektive Unmöglichkeit: Die Leistung ist für jedermann unmöglich (§ 275 Abs. 1 BGB).",
      "Faktische Unmöglichkeit: Aufwand des Schuldners steht in grobem Missverhältnis zum Gläubigerinteresse (§ 275 Abs. 2 BGB).",
      "Subjektive Unmöglichkeit: Nur der konkrete Schuldner ist aus persönlichen Gründen gehindert, ohne dass es sich um eine höchstpersönliche Leistung handelt — stets von § 275 Abs. 1 BGB erfasst.",
      "Persönliche Unzumutbarkeit: Der Schuldner hat die Leistung persönlich zu erbringen und sie ist ihm unzumutbar (§ 275 Abs. 3 BGB).",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 275 BGB enthält drei kodifizierte Tatbestände: (1) Abs. 1: echte (objektive) Unmöglichkeit — Leistung für niemanden mehr möglich (tritt von Gesetzes wegen ein, kein Einrederecht); (2) Abs. 2: faktische Unmöglichkeit — unverhältnismäßiger Aufwand (Einrede); (3) Abs. 3: persönliche Unzumutbarkeit — höchstpersönliche Leistung (Einrede). Die subjektive Unmöglichkeit (nur der konkrete Schuldner kann nicht leisten, ohne Höchstpersönlichkeit) wird von § 275 Abs. 1 BGB mitumfasst, ist aber kein eigenständiger Absatz; das macht Option C ungenau ('stets von § 275 Abs. 1 erfasst' trifft nur für die echte Unmöglichkeit zu, nicht für bloß subjektive Hindernisse, die den Schuldner treffen).",
    sourceIds: ["bgb275"],
    sources: [
      { id: "bgb275", label: "§ 275 BGB", url: "https://www.gesetze-im-internet.de/bgb/__275.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "unmoeglichkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-016",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Rücktritt",
    title: "Rücktrittsfolgen (§§ 346, 347 BGB) — Mehrfachauswahl",
    prompt:
      "Welche der folgenden Rechtsfolgen treten nach einem wirksamen Rücktritt nach § 346 BGB ein? (Mehrere Antworten möglich)",
    options: [
      "Empfangene Leistungen sind zurückzugewähren; gezogene Nutzungen sind herauszugeben (§ 346 Abs. 1 BGB).",
      "Der Rücktritt wirkt rückwirkend ex tunc und macht den Vertrag von Anfang an nichtig.",
      "Ist Herausgabe oder Rückgabe der empfangenen Leistung unmöglich, hat der Rücktrittsverpflichtete Wertersatz zu leisten (§ 346 Abs. 2 BGB).",
      "Nach dem Rücktritt ist kein Schadensersatz mehr möglich; § 325 BGB gilt nur für Vollkaufleute.",
    ],
    answers: [0, 2],
    explanation:
      "§ 346 Abs. 1 BGB: Nach Rücktritt sind die empfangenen Leistungen zurückzugewähren und gezogene Nutzungen herauszugeben. § 346 Abs. 2 BGB: Wertersatz ist geschuldet, wenn Herausgabe/Rückgabe unmöglich ist. Der Rücktritt wirkt nicht ex tunc (d.h. er macht den Vertrag nicht rückwirkend nichtig); er wandelt das Schuldverhältnis in ein Rückgewährschuldverhältnis um (ex nunc). § 325 BGB: Das Recht auf Schadensersatz wird durch den Rücktritt nicht ausgeschlossen — gilt allgemein, nicht nur für Kaufleute.",
    sourceIds: ["bgb346", "bgb325"],
    sources: [
      { id: "bgb346", label: "§ 346 BGB", url: "https://www.gesetze-im-internet.de/bgb/__346.html" },
      { id: "bgb325", label: "§ 325 BGB", url: "https://www.gesetze-im-internet.de/bgb/__325.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["schuldrecht-at", "ruecktritt"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
  {
    id: "hj-schuld-017",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Abtretung",
    title: "Grundsätze der Abtretung (§§ 398, 404, 407 BGB) — Mehrfachauswahl",
    prompt:
      "Welche Aussagen zu den Grundsätzen der Abtretung sind richtig? (Mehrere Antworten möglich)",
    options: [
      "Mit dem Abtretungsvertrag (§ 398 BGB) geht die Forderung durch Rechtsgeschäft (nicht kraft Gesetzes) auf den neuen Gläubiger über; die Mitwirkung des Schuldners ist nicht erforderlich.",
      "Der Schuldner kann dem neuen Gläubiger alle Einwendungen entgegenhalten, die ihm zur Zeit der Abtretung gegen den bisherigen Gläubiger zustanden (§ 404 BGB).",
      "Leistet der Schuldner an den alten Gläubiger, bevor er von der Abtretung Kenntnis hat, wird er nach § 407 Abs. 1 BGB befreit.",
      "Die Abtretung ist nur wirksam, wenn sie notariell beurkundet wird.",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 398 S. 2 BGB: Die Forderung geht mit dem Abtretungsvertrag auf den neuen Gläubiger über — formlos, ohne Mitwirkung des Schuldners. § 404 BGB: Der Schuldner kann dem neuen Gläubiger alle Einwendungen entgegenhalten, die zur Zeit der Abtretung gegen den bisherigen Gläubiger bestanden. § 407 Abs. 1 BGB: Der neue Gläubiger muss eine Leistung, die der Schuldner nach der Abtretung in Unkenntnis an den alten Gläubiger erbringt, gegen sich gelten lassen. Formzwang (Notarpflicht) gibt es für die Abtretung grundsätzlich nicht (Ausnahmen z.B. bei gleichzeitiger Grundschuldübertragung).",
    sourceIds: ["bgb398", "bgb404", "bgb407"],
    sources: [
      { id: "bgb398", label: "§ 398 BGB", url: "https://www.gesetze-im-internet.de/bgb/__398.html" },
      { id: "bgb404", label: "§ 404 BGB", url: "https://www.gesetze-im-internet.de/bgb/__404.html" },
      { id: "bgb407", label: "§ 407 BGB", url: "https://www.gesetze-im-internet.de/bgb/__407.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "abtretung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-018",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Schadensersatzpositionen — Mehrfachauswahl",
    prompt:
      "Welche der folgenden Positionen sind als Schaden nach dem BGB ersatzfähig? (Mehrere Antworten möglich)",
    options: [
      "Entgangener Gewinn nach § 252 BGB.",
      "Immaterieller Schaden (Schmerzensgeld) nach § 253 Abs. 2 BGB bei Verletzung von Körper, Gesundheit, Freiheit oder sexueller Selbstbestimmung.",
      "Vergebliche Aufwendungen nach § 284 BGB statt des Schadensersatzes statt der Leistung.",
      "Sämtliche immateriellen Schäden generell ohne weitere Voraussetzungen.",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 252 S. 1 BGB: Der zu ersetzende Schaden umfasst auch den entgangenen Gewinn. § 253 Abs. 2 BGB: Bei Verletzung von Körper, Gesundheit, Freiheit oder sexueller Selbstbestimmung kann eine billige Entschädigung in Geld auch wegen des Schadens verlangt werden, der nicht Vermögensschaden ist (Schmerzensgeld). § 284 BGB: Statt des Schadensersatzes statt der Leistung kann der Gläubiger vergebliche Aufwendungen ersetzt verlangen, die er im Vertrauen auf den Erhalt der Leistung gemacht hat. § 253 Abs. 1 BGB: Immaterieller Schaden ist nur in den durch das Gesetz bestimmten Fällen ersatzfähig — nicht generell.",
    sourceIds: ["bgb252", "bgb253", "bgb284"],
    sources: [
      { id: "bgb252", label: "§ 252 BGB", url: "https://www.gesetze-im-internet.de/bgb/__252.html" },
      { id: "bgb253", label: "§ 253 BGB", url: "https://www.gesetze-im-internet.de/bgb/__253.html" },
      { id: "bgb284", label: "§ 284 BGB", url: "https://www.gesetze-im-internet.de/bgb/__284.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "schadensrecht"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
  {
    id: "hj-schuld-019",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Schuldverhältnis",
    title: "Entstehungsgründe eines Schuldverhältnisses (§ 311 BGB) — Mehrfachauswahl",
    prompt:
      "Aus welchen der folgenden Gründe kann ein Schuldverhältnis mit Pflichten nach § 241 Abs. 2 BGB entstehen, auch ohne dass ein Hauptleistungsaustausch beabsichtigt ist? (Mehrere Antworten möglich)",
    options: [
      "Aufnahme von Vertragsverhandlungen (§ 311 Abs. 2 Nr. 1 BGB).",
      "Anbahnung eines Vertrags durch Geschäftskontakt (§ 311 Abs. 2 Nr. 2 BGB).",
      "Inanspruchnahme besonderen Vertrauens durch einen Dritten, der nicht selbst Vertragspartei werden soll (§ 311 Abs. 3 BGB, z.B. Sachwalter/Sachverständiger mit Eigeninteresse).",
      "Jede zufällige Begegnung zweier Personen im öffentlichen Raum.",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 311 Abs. 2 BGB: Schuldverhältnisse mit Rücksichtnahmepflichten entstehen aus (Nr. 1) der Aufnahme von Vertragsverhandlungen, (Nr. 2) der Anbahnung eines Vertrags durch Geschäftskontakt und (Nr. 3) ähnlichen geschäftlichen Kontakten. § 311 Abs. 3 BGB: Ein Schuldverhältnis kann auch mit Personen entstehen, die nicht Vertragspartei werden sollen, wenn sie im besonderen Maße Vertrauen in Anspruch nehmen (z.B. Sachverständige, Vermittler). Eine zufällige Begegnung im öffentlichen Raum begründet kein Schuldverhältnis nach § 311 BGB.",
    sourceIds: ["bgb311"],
    sources: [
      { id: "bgb311", label: "§ 311 BGB", url: "https://www.gesetze-im-internet.de/bgb/__311.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "cic", "schuldverhaeltnis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-020",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Leistungsstörungen",
    title: "Schadensersatz statt der Leistung — Normgefüge (§§ 280 III, 281–283) — Mehrfachauswahl",
    prompt:
      "Welche der folgenden Normen regeln jeweils eigenständige Tatbestände für Schadensersatz statt der Leistung? (Mehrere Antworten möglich)",
    options: [
      "§§ 280 Abs. 3, 281 BGB: Schadensersatz statt der Leistung bei ausbleibender Leistung nach Fristsetzung.",
      "§§ 280 Abs. 3, 282 BGB: Schadensersatz statt der Leistung bei Verletzung einer Pflicht nach § 241 Abs. 2 BGB, wenn dem Gläubiger Festhalten am Vertrag unzumutbar ist.",
      "§§ 280 Abs. 3, 283 BGB: Schadensersatz statt der Leistung bei nachträglicher Unmöglichkeit (§ 275 BGB), ohne Fristsetzung.",
      "§ 280 Abs. 1 BGB allein: Schadensersatz statt der Leistung, ohne weitere Voraussetzungen.",
    ],
    answers: [0, 1, 2],
    explanation:
      "§ 280 Abs. 3 BGB: Schadensersatz statt der Leistung kann der Gläubiger nur unter den zusätzlichen Voraussetzungen des § 281, des § 282 oder des § 283 verlangen. § 281 BGB: Fristsetzung, fruchtloser Ablauf. § 282 BGB: Verletzung einer Nebenpflicht (§ 241 Abs. 2 BGB), die dem Gläubiger Festhalten am Vertrag unzumutbar macht. § 283 BGB: Nachträgliche Unmöglichkeit (§ 275 BGB) — Fristsetzung entbehrlich. § 280 Abs. 1 BGB allein begründet nur einfachen Schadensersatz (z.B. Verzögerungsschaden mit § 286, Begleitschäden), nicht den Schadensersatz statt der Leistung.",
    sourceIds: ["bgb280", "bgb281", "bgb282", "bgb283"],
    sources: [
      { id: "bgb280", label: "§ 280 BGB", url: "https://www.gesetze-im-internet.de/bgb/__280.html" },
      { id: "bgb281", label: "§ 281 BGB", url: "https://www.gesetze-im-internet.de/bgb/__281.html" },
      { id: "bgb282", label: "§ 282 BGB", url: "https://www.gesetze-im-internet.de/bgb/__282.html" },
      { id: "bgb283", label: "§ 283 BGB", url: "https://www.gesetze-im-internet.de/bgb/__283.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "leistungsstoerungen", "schadensersatz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-021",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "multi",
    topic: "Schuldrecht AT - Anfängliche Unmöglichkeit",
    title: "Anfängliche Unmöglichkeit (§ 311a BGB) — Mehrfachauswahl",
    prompt:
      "Welche Aussagen zur anfänglichen Unmöglichkeit nach § 311a BGB sind richtig? (Mehrere Antworten möglich)",
    options: [
      "§ 311a Abs. 1 BGB: Der Vertrag ist wirksam, obwohl die Leistung von Anfang an unmöglich war — er ist nicht automatisch nichtig.",
      "§ 311a Abs. 2 BGB: Der Gläubiger kann Schadensersatz statt der Leistung verlangen, wenn der Schuldner das Hindernis kannte oder kennen musste.",
      "§ 311a BGB setzt stets eine Fristsetzung des Gläubigers voraus.",
      "Die anfängliche Unmöglichkeit führt anders als die nachträgliche immer zur Nichtigkeit des Vertrags nach § 306 BGB a.F.",
    ],
    answers: [0, 1],
    explanation:
      "§ 311a Abs. 1 BGB: Der Schuldner ist auch dann zur Lieferung verpflichtet (und der Vertrag ist wirksam), wenn das Leistungshindernis schon bei Vertragsschluss bestand. Eine Nichtigkeit ex tunc (wie nach § 306 BGB a.F.) gibt es nicht mehr. § 311a Abs. 2 S. 1 BGB: Der Gläubiger kann Schadensersatz statt der Leistung oder Ersatz vergeblicher Aufwendungen verlangen, wenn der Schuldner das Hindernis bei Vertragsschluss kannte oder kennen musste. Eine Fristsetzung ist im Rahmen des § 311a BGB nicht erforderlich; sie ist nur bei § 281 BGB vorgesehen (nachträgliche Nicht-Leistung). § 306 BGB a.F. (impossibilium nulla obligatio) ist abgeschafft.",
    sourceIds: ["bgb311a"],
    sources: [
      { id: "bgb311a", label: "§ 311a BGB", url: "https://www.gesetze-im-internet.de/bgb/__311a.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["schuldrecht-at", "unmoeglichkeit", "leistungsstoerungen"],
    reviewStatus: "beta-ready",
  },

  // ─── FLASHCARDS hj-schuld-022 … hj-schuld-031 ────────────────────────────

  {
    id: "hj-schuld-022",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Schuldverhältnis & Pflichten",
    title: "§ 241a BGB — Zusendung unbestellter Waren",
    prompt:
      "Was regelt § 241a BGB und welche Rechtsfolge ergibt sich für den Verbraucher?",
    options: [],
    answers: [],
    explanation:
      "§ 241a Abs. 1 BGB: Durch die Lieferung unbestellter Sachen oder durch die Erbringung unbestellter sonstiger Leistungen durch einen Unternehmer an einen Verbraucher wird ein Anspruch gegen den Verbraucher nicht begründet. Die unbestellte Leistung löst also keine Zahlungspflicht aus und begründet kein Schuldverhältnis zugunsten des Unternehmers. Der Verbraucher ist nicht zur Rücksendung verpflichtet; er haftet lediglich für Vorsatz und grobe Fahrlässigkeit bei Verschlechterung (§ 241a Abs. 2 BGB). Ziel der Norm ist der Schutz des Verbrauchers vor unbestellten Waren ('Unbestelltensendungen').",
    sourceIds: ["bgb241a"],
    sources: [
      { id: "bgb241a", label: "§ 241a BGB", url: "https://www.gesetze-im-internet.de/bgb/__241a.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "schuldverhaeltnis"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-023",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Verzug",
    title: "Verzugszinsen (§ 288 BGB)",
    prompt:
      "Wie hoch ist der gesetzliche Verzugszinssatz nach § 288 BGB für Geldschulden zwischen Verbrauchern (§ 288 Abs. 1) bzw. bei Rechtsgeschäften ohne Verbraucherbeteiligung (§ 288 Abs. 2)?",
    options: [],
    answers: [],
    explanation:
      "§ 288 Abs. 1 S. 2 BGB: Eine Geldschuld ist während des Verzugs mit 5 Prozentpunkten über dem Basiszinssatz nach § 247 BGB pro Jahr zu verzinsen (Verbrauchergeschäfte und einseitige Handelsgeschäfte). § 288 Abs. 2 BGB: Bei Rechtsgeschäften, an denen ein Verbraucher nicht beteiligt ist (B2B), beträgt der Zinssatz 9 Prozentpunkte über dem Basiszinssatz. Der Basiszinssatz wird von der Deutschen Bundesbank nach § 247 BGB halbjährlich (zum 1. Januar und 1. Juli) angepasst. Die Verzugszinsen entstehen kraft Gesetzes ab Verzugseintritt (§ 286 BGB), ohne weiteren Nachweis eines Schadens.",
    sourceIds: ["bgb288"],
    sources: [
      { id: "bgb288", label: "§ 288 BGB", url: "https://www.gesetze-im-internet.de/bgb/__288.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-024",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Leistungsstörungen",
    title: "Vertretenmüssen (§§ 276, 278 BGB)",
    prompt:
      "Was versteht man unter 'Vertretenmüssen' im Schuldrecht und was regeln §§ 276, 278 BGB?",
    options: [],
    answers: [],
    explanation:
      "§ 276 Abs. 1 S. 1 BGB: Der Schuldner hat Vorsatz und Fahrlässigkeit zu vertreten, wenn eine strengere oder mildere Haftung weder bestimmt noch aus dem sonstigen Inhalt des Schuldverhältnisses zu entnehmen ist. § 276 Abs. 2 BGB: Fahrlässig handelt, wer die im Verkehr erforderliche Sorgfalt außer Acht lässt. § 278 BGB: Der Schuldner hat ein Verschulden seines gesetzlichen Vertreters und der Personen, derer er sich zur Erfüllung seiner Verbindlichkeit bedient (Erfüllungsgehilfe), in gleichem Umfang zu vertreten wie eigenes Verschulden. Die Haftung für Erfüllungsgehilfen nach § 278 BGB setzt kein Auswahlverschulden voraus (Unterschied zu § 831 BGB — deliktsrechtliche Haftung für Verrichtungsgehilfen).",
    sourceIds: ["bgb276", "bgb278"],
    sources: [
      { id: "bgb276", label: "§ 276 BGB", url: "https://www.gesetze-im-internet.de/bgb/__276.html" },
      { id: "bgb278", label: "§ 278 BGB", url: "https://www.gesetze-im-internet.de/bgb/__278.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "pflichtverletzung", "leistungsstoerungen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-025",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Gefahrtragung",
    title: "Befreiung von der Gegenleistung bei Unmöglichkeit (§ 326 BGB)",
    prompt:
      "Was regelt § 326 Abs. 1 BGB für den Fall, dass der Schuldner nach § 275 BGB nicht zu leisten braucht?",
    options: [],
    answers: [],
    explanation:
      "§ 326 Abs. 1 S. 1 BGB: Braucht der Schuldner nach § 275 Abs. 1 bis 3 BGB nicht zu leisten, entfällt der Anspruch auf die Gegenleistung. Das ist die Risikoverteilung im gegenseitigen Vertrag: Wird die Leistung unmöglich, muss der Gläubiger die Gegenleistung nicht mehr erbringen (Prinzip: Preisgefahr trägt der Schuldner bis zur Übergabe). § 326 Abs. 2 BGB: Ausnahme, wenn der Gläubiger die Unmöglichkeit zu vertreten hat oder sich in Annahmeverzug befindet — dann bleibt der Gegenleistungsanspruch bestehen. § 326 Abs. 4 BGB: Bereits Geleistetes kann nach § 346 ff. BGB (Rücktritt) oder § 812 BGB (Bereicherungsrecht) zurückgefordert werden.",
    sourceIds: ["bgb326"],
    sources: [
      { id: "bgb326", label: "§ 326 BGB", url: "https://www.gesetze-im-internet.de/bgb/__326.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["schuldrecht-at", "unmoeglichkeit", "gefahrtragung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-026",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Erlöschen",
    title: "Erlass (§ 397 BGB)",
    prompt:
      "Was ist der Erlass nach § 397 BGB und wie unterscheidet er sich von der Aufrechnung?",
    options: [],
    answers: [],
    explanation:
      "§ 397 Abs. 1 BGB: Das Schuldverhältnis erlischt, wenn der Gläubiger dem Schuldner durch Vertrag die Schuld erlässt. Der Erlass ist ein Erlassvertrag (Einigkeit von Gläubiger und Schuldner über das Erlöschen der Forderung); er erfordert die Mitwirkung des Schuldners (da eine Forderung nicht einseitig vom Gläubiger aufgegeben werden kann, ohne dass der Schuldner zustimmt). Abgrenzung zur Aufrechnung (§§ 387 ff. BGB): Die Aufrechnung ist einseitige Erklärung des Aufrechnenden und setzt eine Gegenforderung voraus; der Erlass ist ein Vertrag und setzt keine Gegenforderung voraus — der Gläubiger verzichtet ohne Gegenleistung. § 397 Abs. 2 BGB: Einem Erlassvertrag steht es gleich, wenn der Gläubiger durch Vertrag mit dem Schuldner anerkennt, dass das Schuldverhältnis nicht bestehe (negatives Schuldanerkenntnis).",
    sourceIds: ["bgb397"],
    sources: [
      { id: "bgb397", label: "§ 397 BGB", url: "https://www.gesetze-im-internet.de/bgb/__397.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "erlöschen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-027",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Immaterieller Schaden und Schmerzensgeld (§ 253 BGB)",
    prompt:
      "Wann ist nach § 253 BGB immaterieller Schaden zu ersetzen und was ist der Anwendungsbereich von § 253 Abs. 2 BGB?",
    options: [],
    answers: [],
    explanation:
      "§ 253 Abs. 1 BGB: Wegen eines Schadens, der nicht Vermögensschaden ist, kann Entschädigung in Geld nur in den durch das Gesetz bestimmten Fällen gefordert werden (Enumerationsprinzip). § 253 Abs. 2 BGB: Ist wegen einer Verletzung des Körpers, der Gesundheit, der Freiheit oder der sexuellen Selbstbestimmung Schadensersatz zu leisten, kann auch wegen des Schadens, der nicht Vermögensschaden ist, eine billige Entschädigung in Geld gefordert werden (Schmerzensgeld). § 253 Abs. 2 BGB gilt für alle Schadensersatzansprüche, gleich ob aus Vertrag (z.B. § 280 BGB) oder Delikt (z.B. § 823 BGB), soweit die genannten Rechtsgüter verletzt sind. Der Begriff 'billig' bedeutet, dass das Gericht nach freiem Ermessen unter Berücksichtigung aller Umstände eine angemessene Entschädigung festsetzt.",
    sourceIds: ["bgb253"],
    sources: [
      { id: "bgb253", label: "§ 253 BGB", url: "https://www.gesetze-im-internet.de/bgb/__253.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "schadensrecht"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
  {
    id: "hj-schuld-028",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Rücktritt",
    title: "Rücktrittserklärung (§ 349 BGB) und Ausübung",
    prompt:
      "Wie ist der Rücktritt nach § 349 BGB auszuüben und bedarf er einer Begründung?",
    options: [],
    answers: [],
    explanation:
      "§ 349 BGB: Der Rücktritt erfolgt durch Erklärung gegenüber dem anderen Teil. Die Rücktrittserklärung ist eine einseitige empfangsbedürftige Willenserklärung; sie bedarf keiner Form (Formfreiheit) und keiner Begründung. Sie ist unwiderruflich und gestaltet das Schuldverhältnis in ein Rückgewährschuldverhältnis um (§ 346 BGB). Ein Rücktrittsrecht setzt die gesetzlichen Voraussetzungen (z.B. § 323 BGB: Fristsetzung + fruchtloser Ablauf) oder eine vertragliche Vereinbarung voraus. Ohne Rücktrittsrecht ist die Erklärung unwirksam. Abgrenzung: Die Kündigung beendet ein Dauerschuldverhältnis nur für die Zukunft (ex nunc). Der Rücktritt dagegen wandelt das Schuldverhältnis insgesamt in ein Rückgewährschuldverhältnis um (§ 346 BGB) — bereits erbrachte Leistungen sind zurückzugewähren; anders als die Anfechtung (§ 142 BGB) führt er nicht zur rückwirkenden Nichtigkeit (kein ex tunc).",
    sourceIds: ["bgb349", "bgb323"],
    sources: [
      { id: "bgb349", label: "§ 349 BGB", url: "https://www.gesetze-im-internet.de/bgb/__349.html" },
      { id: "bgb323", label: "§ 323 BGB", url: "https://www.gesetze-im-internet.de/bgb/__323.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["schuldrecht-at", "ruecktritt"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-029",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Leistungsstörungen",
    title: "Schadensersatz statt der Leistung bei Nebenpflichtverletzung (§ 282 BGB)",
    prompt:
      "Wann kann der Gläubiger nach § 282 BGB Schadensersatz statt der Leistung verlangen, ohne Frist setzen zu müssen?",
    options: [],
    answers: [],
    explanation:
      "§ 282 BGB: Beruht die Pflichtverletzung auf einer Verletzung einer Pflicht nach § 241 Abs. 2 BGB (Rücksichtnahmepflicht) und ist dem Gläubiger die Leistung durch den Schuldner nicht zuzumuten, kann der Gläubiger gemäß § 280 Abs. 1 und 3 BGB Schadensersatz statt der Leistung verlangen. Voraussetzungen: (1) Schuldverhältnis, (2) Verletzung einer Nebenpflicht/Rücksichtnahmepflicht (§ 241 Abs. 2 BGB), (3) Unzumutbarkeit des Festhaltens am Vertrag für den Gläubiger, (4) Vertretenmüssen. Eine Fristsetzung (wie bei § 281 BGB) ist entbehrlich, weil das Vertrauensverhältnis so schwer erschüttert ist, dass eine Nachfrist unzumutbar wäre. Beispiel: Der Schuldner belästigt den Gläubiger oder begeht einen schweren Vertrauensbruch.",
    sourceIds: ["bgb282", "bgb241"],
    sources: [
      { id: "bgb282", label: "§ 282 BGB", url: "https://www.gesetze-im-internet.de/bgb/__282.html" },
      { id: "bgb241", label: "§ 241 BGB", url: "https://www.gesetze-im-internet.de/bgb/__241.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "leistungsstoerungen", "pflichtverletzung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-030",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Vergebliche Aufwendungen (§ 284 BGB)",
    prompt:
      "Was kann der Gläubiger nach § 284 BGB verlangen und in welchem Verhältnis steht das zu § 281 BGB?",
    options: [],
    answers: [],
    explanation:
      "§ 284 BGB: Anstelle des Schadensersatzes statt der Leistung kann der Gläubiger Ersatz der Aufwendungen verlangen, die er im Vertrauen auf den Erhalt der Leistung gemacht hat und billigerweise machen durfte, es sei denn, deren Zweck wäre auch ohne die Pflichtverletzung des Schuldners nicht erreicht worden. § 284 BGB gibt dem Gläubiger eine Alternative: Statt des Schadensersatzes statt der Leistung (§§ 280 III, 281 BGB) kann er vergebliche Aufwendungen ersetzt verlangen — er muss sich also nicht auf die oft schwierige Rentabilitätsvermutung oder einen konkret entgangenen Gewinn berufen. Die Voraussetzungen des § 281 BGB (Fristsetzung etc.) müssen auch für § 284 BGB erfüllt sein (§ 284 BGB enthält kein eigenständiges Pflichtverletzungsregime). Beide Ansprüche können nicht gleichzeitig geltend gemacht werden (Alternativität).",
    sourceIds: ["bgb284"],
    sources: [
      { id: "bgb284", label: "§ 284 BGB", url: "https://www.gesetze-im-internet.de/bgb/__284.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "schadensrecht", "leistungsstoerungen"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-031",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "Schuldrecht AT - Gesamtschuld",
    title: "Gesamtschuldnerische Haftung (§ 421 BGB) — Grundstruktur",
    prompt:
      "Was kennzeichnet die Gesamtschuld nach § 421 BGB und wie unterscheidet sie sich von der Teilschuld?",
    options: [],
    answers: [],
    explanation:
      "§ 421 S. 1 BGB: Schulden mehrere eine Leistung so, dass jeder die ganze Leistung zu bewirken verpflichtet, der Gläubiger sie aber nur einmal zu fordern berechtigt ist (Gesamtschuldner), so kann der Gläubiger die Leistung nach seinem Belieben von jedem der Schuldner ganz oder zu einem Teil fordern. § 421 S. 2 BGB: Bis zur Bewirkung der ganzen Leistung bleiben sämtliche Schuldner verpflichtet. Kern der Gesamtschuld: Außenverhältnis — Gläubiger kann von jedem Gesamtschuldner vollen Betrag verlangen; Innenverhältnis — nach § 426 BGB Ausgleich. Abgrenzung zur Teilschuld: Bei Teilschuld schuldet jeder nur seinen Anteil; der Gläubiger muss von jedem Schuldner seinen Teil einfordern. Die Gesamtschuld ist für den Gläubiger günstiger, da er das Insolvenzrisiko auf die Schuldner verteilen kann.",
    sourceIds: ["bgb421"],
    sources: [
      { id: "bgb421", label: "§ 421 BGB", url: "https://www.gesetze-im-internet.de/bgb/__421.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["schuldrecht-at", "mehrheiten", "gesamtschuld"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERINGS hj-schuld-032 … hj-schuld-036 ─────────────────────────────

  {
    id: "hj-schuld-032",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Schuldrecht AT - Leistungsstörungen",
    title: "Prüfschema: Schadensersatz statt der Leistung (§§ 280 I, III, 281 BGB)",
    prompt:
      "Ordne die Prüfungsschritte für Schadensersatz statt der Leistung wegen Nichtleistung nach §§ 280 Abs. 1, Abs. 3, 281 BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Schuldverhältnis (§ 280 Abs. 1 BGB): wirksames vertragliches oder gesetzliches Schuldverhältnis",
      "Pflichtverletzung (§ 280 Abs. 1 BGB): Schuldner hat die fällige Leistung nicht oder nicht wie geschuldet erbracht",
      "Fristsetzung und fruchtloser Ablauf (§ 281 Abs. 1 BGB): angemessene Nachfrist gesetzt, abgelaufen, Leistung weiter ausgeblieben",
      "Vertretenmüssen (§ 280 Abs. 1 S. 2 BGB): wird vermutet; Schuldner muss sich entlasten",
      "Schaden (§ 280 Abs. 1 BGB): kausaler, adäquater Schaden beim Gläubiger",
    ],
    explanation:
      "Das Prüfschema des § 281 BGB: (1) Schuldverhältnis als Haftungsgrundlage; (2) Pflichtverletzung (Nichtleistung trotz Fälligkeit); (3) Fristsetzung und fruchtloser Ablauf als zusätzliche Voraussetzung nach § 281 Abs. 1 BGB (Abgrenzung zu § 280 Abs. 1 BGB, der keinen 'kleinen' Schadensersatz wegen Nichtleistung gibt); (4) Vertretenmüssen nach § 280 Abs. 1 S. 2 BGB (Beweislastumkehr: Schuldner muss fehlendes Verschulden beweisen); (5) Schaden. Die Fristsetzung ist entbehrlich in den Fällen des § 281 Abs. 2 BGB (ernsthafte und endgültige Weigerung, Interessenfortfall).",
    sourceIds: ["bgb280", "bgb281"],
    sources: [
      { id: "bgb280", label: "§ 280 BGB", url: "https://www.gesetze-im-internet.de/bgb/__280.html" },
      { id: "bgb281", label: "§ 281 BGB", url: "https://www.gesetze-im-internet.de/bgb/__281.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "leistungsstoerungen", "schadensersatz"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-033",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Schuldrecht AT - Verzug",
    title: "Prüfschema: Schuldnerverzug (§ 286 BGB)",
    prompt:
      "Ordne die Prüfungsschritte des Schuldnerverzugs nach § 286 BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Fälliger und durchsetzbarer Anspruch: Die Leistungspflicht muss entstanden und fällig sein; keine Einreden (z.B. § 320 BGB) dürfen entgegenstehen",
      "Mahnung (§ 286 Abs. 1 BGB) oder Mahnungsersatz (§ 286 Abs. 2 BGB): formfreie Leistungsaufforderung nach Fälligkeit oder Mahnungsentbehrlichkeit (Kalendertermin, Verweigerung etc.)",
      "Nichtleistung trotz Mahnung: Schuldner erbringt die Leistung nicht",
      "Vertretenmüssen (§ 286 Abs. 4 BGB): Schuldner hat die Nichtleistung zu vertreten; fehlendes Vertretenmüssen schließt Verzug aus",
    ],
    explanation:
      "Schuldnerverzug nach § 286 BGB setzt voraus: (1) Fällige, durchsetzbare Leistungspflicht — Einreden (§ 320 BGB: Einrede des nicht erfüllten Vertrags; § 273 BGB: Zurückbehaltungsrecht) stehen dem Verzug entgegen. (2) Mahnung oder Mahnungsersatz — § 286 Abs. 2 BGB (Nr. 1: Kalendertermin; Nr. 2: Berechnung nach Ereignis; Nr. 3: ernsthafte Verweigerung; Nr. 4: besondere Gründe). (3) Tatsächliche Nichtleistung. (4) Vertretenmüssen — § 286 Abs. 4 BGB stellt klar, dass Verzug ausscheidet, wenn die Nichtleistung nicht zu vertreten ist. Rechtsfolgen: Schadensersatz für Verzögerungsschaden (§§ 280 I, II, 286 BGB), Verzugszinsen (§ 288 BGB), Haftungsverschärfung (§ 287 BGB).",
    sourceIds: ["bgb286"],
    sources: [
      { id: "bgb286", label: "§ 286 BGB", url: "https://www.gesetze-im-internet.de/bgb/__286.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-034",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Schuldrecht AT - Rücktritt",
    title: "Prüfschema: Rücktritt nach § 323 BGB",
    prompt:
      "Ordne die Prüfungsschritte des gesetzlichen Rücktrittsrechts nach § 323 BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Gegenseitiger Vertrag (§ 323 Abs. 1 BGB): wirksamer gegenseitiger Vertrag (z.B. Kauf, Miete, Werkvertrag)",
      "Fällige Leistungspflicht: Schuldner hat eine fällige Leistungs- oder Teilleistungspflicht nicht oder nicht wie geschuldet erbracht",
      "Angemessene Nachfristsetzung und fruchtloser Ablauf (§ 323 Abs. 1 BGB): oder Entbehrlichkeit der Fristsetzung (§ 323 Abs. 2 BGB, z.B. ernsthafte Weigerung)",
      "Rücktrittserklärung (§ 349 BGB): formfreie, empfangsbedürftige Erklärung gegenüber dem Vertragspartner",
    ],
    explanation:
      "§ 323 Abs. 1 BGB: Erbringt bei einem gegenseitigen Vertrag der Schuldner eine fällige Leistung nicht oder nicht wie geschuldet, so kann der Gläubiger vom Vertrag zurücktreten, wenn er dem Schuldner erfolglos eine angemessene Frist zur Leistung oder Nacherfüllung bestimmt hat. § 323 Abs. 2 BGB: Fristsetzung ist entbehrlich bei ernsthafter und endgültiger Weigerung (Nr. 1), Interessenfortfall (Nr. 2), besonderen Umständen (Nr. 3). § 349 BGB: Die Rücktrittserklärung ist empfangsbedürftig und formfrei. Kein Vertretenmüssen des Schuldners nötig (Abgrenzung zu § 281 BGB).",
    sourceIds: ["bgb323", "bgb349"],
    sources: [
      { id: "bgb323", label: "§ 323 BGB", url: "https://www.gesetze-im-internet.de/bgb/__323.html" },
      { id: "bgb349", label: "§ 349 BGB", url: "https://www.gesetze-im-internet.de/bgb/__349.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "ruecktritt"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-035",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Schuldrecht AT - Aufrechnung",
    title: "Prüfschema: Aufrechnung (§§ 387, 388 BGB)",
    prompt:
      "Ordne die Prüfungsschritte einer Aufrechnung nach §§ 387, 388 BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Gegenseitigkeit: Gläubiger der Hauptforderung ist zugleich Schuldner der Gegenforderung (und umgekehrt)",
      "Gleichartigkeit der Leistungsgegenstände (§ 387 BGB): typischerweise Geldforderungen gegen Geldforderungen",
      "Fälligkeit der Gegenforderung und Erfüllbarkeit der Hauptforderung (§ 387 BGB)",
      "Kein Aufrechnungsverbot (§§ 390, 393–394 BGB): z.B. keine verjährte Gegenforderung entgegen § 215 BGB; keine Aufrechnung gegen Unterhaltsforderungen § 394 BGB",
      "Aufrechnungserklärung (§ 388 BGB): empfangsbedürftige Willenserklärung; rückwirkende Tilgung ab dem Zeitpunkt der Aufrechnungslage (§ 389 BGB)",
    ],
    explanation:
      "Die Aufrechnung nach § 387 BGB setzt Gegenseitigkeit, Gleichartigkeit, Fälligkeit der Gegenforderung und Erfüllbarkeit der Hauptforderung voraus. § 388 BGB: Erklärung gegenüber dem anderen Teil erforderlich. § 389 BGB: Die Aufrechnung wirkt ex tunc — auf den Zeitpunkt zurück, in dem die Aufrechnungslage entstanden ist. Aufrechnungsverbote: § 390 BGB (einredebehaftete Gegenforderungen), § 393 BGB (Aufrechnung gegen deliktische Ansprüche aus vorsätzlicher unerlaubter Handlung), § 394 BGB (Aufrechnung gegen unpfändbare Forderungen). § 215 BGB: Verjährte Gegenforderungen können aufgerechnet werden, wenn die Aufrechnungslage vor Verjährungseintritt bestand.",
    sourceIds: ["bgb387", "bgb388", "bgb389"],
    sources: [
      { id: "bgb387", label: "§ 387 BGB", url: "https://www.gesetze-im-internet.de/bgb/__387.html" },
      { id: "bgb388", label: "§ 388 BGB", url: "https://www.gesetze-im-internet.de/bgb/__388.html" },
      { id: "bgb389", label: "§ 389 BGB", url: "https://www.gesetze-im-internet.de/bgb/__389.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "erlöschen", "aufrechnung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-036",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "Schuldrecht AT - Schadensrecht",
    title: "Prüfschema: Schadensumfang (§§ 249 ff. BGB)",
    prompt:
      "Ordne die Prüfungsschritte zur Bestimmung des ersatzfähigen Schadensumfangs nach §§ 249 ff. BGB in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Haftungsbegründende Kausalität: Pflichtverletzung hat den Schaden (als Verletzungserfolg) kausal verursacht",
      "Haftungsausfüllende Kausalität: Art und Umfang des Schadens bestimmen — Differenzhypothese (Vermögenslage mit und ohne Schadensereignis)",
      "Schadensart: Naturalrestitution (§ 249 Abs. 1 BGB) vorrangig; bei Körper-/Sachschaden: Wahlrecht Geldersatz (§ 249 Abs. 2 BGB); subsidiär Geldersatz bei Unmöglichkeit/Unzulänglichkeit (§ 251 BGB)",
      "Mitverschulden (§ 254 BGB): Minderung des Anspruchs nach Verursachungsanteilen",
    ],
    explanation:
      "Der Schadensersatzumfang folgt einer eigenen Prüfungssequenz: (1) Haftungsbegründende Kausalität — war die Pflichtverletzung ursächlich für den Schadenseintritt (Äquivalenz- und Adäquanztheorie)? (2) Differenzhypothese — Vergleich der Vermögenslagen mit und ohne Schadensereignis. (3) Schadensart: § 249 Abs. 1 BGB (Naturalrestitution als Leitprinzip), § 249 Abs. 2 BGB (Geldersatz-Option bei Sach-/Personenschäden), § 251 Abs. 1 BGB (Geldersatz zwingend bei Herstellungsunmöglichkeit), § 251 Abs. 2 BGB (Geldersatz bei unverhältnismäßigen Herstellungskosten). (4) § 254 BGB: Mitverschulden des Geschädigten mindert den Anspruch quotal.",
    sourceIds: ["bgb249", "bgb251", "bgb254"],
    sources: [
      { id: "bgb249", label: "§ 249 BGB", url: "https://www.gesetze-im-internet.de/bgb/__249.html" },
      { id: "bgb251", label: "§ 251 BGB", url: "https://www.gesetze-im-internet.de/bgb/__251.html" },
      { id: "bgb254", label: "§ 254 BGB", url: "https://www.gesetze-im-internet.de/bgb/__254.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["schuldrecht-at", "schadensrecht"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZES hj-schuld-037 … hj-schuld-041 ────────────────────────────────

  {
    id: "hj-schuld-037",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Schuldrecht AT - Verzug",
    title: "Verzugszinssatz Verbraucher (§ 288 Abs. 1 BGB)",
    prompt:
      "Eine Geldschuld ist während des Verzugs nach § 288 Abs. 1 S. 2 BGB mit ___ über dem Basiszinssatz nach § 247 BGB zu verzinsen.",
    options: [
      "3 Prozentpunkten",
      "5 Prozentpunkten",
      "9 Prozentpunkten",
      "12 Prozentpunkten",
    ],
    answers: [1],
    explanation:
      "§ 288 Abs. 1 S. 2 BGB: Der Zinssatz beträgt für das Jahr 5 Prozentpunkte über dem Basiszinssatz. Dies gilt für alle Geldschulden, soweit kein Verbraucherausschluss nach § 288 Abs. 2 BGB eingreift (B2B: 9 Prozentpunkte). Der Basiszinssatz wird gemäß § 247 BGB halbjährlich von der Deutschen Bundesbank festgesetzt. Verzugszinsen entstehen kraft Gesetzes ab Verzugseintritt ohne Nachweis eines konkreten Schadens; weitergehender Schaden kann nach § 288 Abs. 4 i.V.m. § 286 BGB geltend gemacht werden.",
    sourceIds: ["bgb288"],
    sources: [
      { id: "bgb288", label: "§ 288 BGB", url: "https://www.gesetze-im-internet.de/bgb/__288.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-038",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Schuldrecht AT - Verzug",
    title: "Verzugszinssatz B2B (§ 288 Abs. 2 BGB)",
    prompt:
      "Bei Rechtsgeschäften, an denen kein Verbraucher beteiligt ist, beträgt der Verzugszinssatz nach § 288 Abs. 2 BGB ___ über dem Basiszinssatz.",
    options: [
      "3 Prozentpunkte",
      "5 Prozentpunkte",
      "9 Prozentpunkte",
      "15 Prozentpunkte",
    ],
    answers: [2],
    explanation:
      "§ 288 Abs. 2 BGB: Bei Rechtsgeschäften, an denen ein Verbraucher nicht beteiligt ist (Unternehmer-zu-Unternehmer-Geschäfte, B2B), beträgt der Zinssatz für Entgeltforderungen 9 Prozentpunkte über dem Basiszinssatz. Hintergrund: Die höhere Rate soll Anreize zur pünktlichen Zahlung im Handelsverkehr setzen (Umsetzung der EU-Zahlungsverzugsrichtlinie 2011/7/EU). § 288 Abs. 1 BGB (5 Prozentpunkte) gilt als Grundregel für Verbrauchergeschäfte.",
    sourceIds: ["bgb288"],
    sources: [
      { id: "bgb288", label: "§ 288 BGB", url: "https://www.gesetze-im-internet.de/bgb/__288.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["schuldrecht-at", "verzug"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-039",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Schuldrecht AT - Unmöglichkeit",
    title: "Ausschlusswirkung § 275 Abs. 1 BGB",
    prompt:
      "Nach § 275 Abs. 1 BGB ist der Anspruch auf Leistung ___, soweit diese für den Schuldner oder jedermann unmöglich ist.",
    options: [
      "einredebehaftet",
      "ausgeschlossen",
      "aufzuschieben",
      "verjährt",
    ],
    answers: [1],
    explanation:
      "§ 275 Abs. 1 BGB: Der Anspruch auf Leistung ist ausgeschlossen, soweit diese für den Schuldner oder jedermann unmöglich ist. Die Formulierung 'ausgeschlossen' bedeutet, dass die Leistungspflicht von Gesetzes wegen entfällt — es handelt sich um eine Einwendung (von Amts wegen zu beachten), nicht um eine Einrede (die nur auf Antrag zu berücksichtigen wäre). Im Unterschied dazu sind § 275 Abs. 2 und 3 BGB als Einrederechte ausgestaltet: Der Schuldner muss sich aktiv auf die faktische Unmöglichkeit bzw. Unzumutbarkeit berufen.",
    sourceIds: ["bgb275"],
    sources: [
      { id: "bgb275", label: "§ 275 BGB", url: "https://www.gesetze-im-internet.de/bgb/__275.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["schuldrecht-at", "unmoeglichkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-040",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Schuldrecht AT - Erlöschen",
    title: "Erlöschen durch Erfüllung (§ 362 Abs. 1 BGB)",
    prompt:
      "Das Schuldverhältnis erlischt nach § 362 Abs. 1 BGB, wenn die ___ Leistung an den Gläubiger bewirkt wird.",
    options: [
      "mögliche",
      "geschuldete",
      "zumutbare",
      "gleichartige",
    ],
    answers: [1],
    explanation:
      "§ 362 Abs. 1 BGB: Das Schuldverhältnis erlischt, wenn die geschuldete Leistung an den Gläubiger bewirkt wird. 'Geschuldet' bedeutet: die Leistung muss inhaltlich, qualitativ und quantitativ dem entsprechen, was nach dem Schuldverhältnis zu erbringen ist. Drittleistung kann nach § 267 BGB zur Erfüllung führen, wenn der Gläubiger nicht widerspricht. Annahme an Erfüllungs statt (§ 364 Abs. 1 BGB) setzt die Annahme einer anderen als der geschuldeten Leistung voraus.",
    sourceIds: ["bgb362"],
    sources: [
      { id: "bgb362", label: "§ 362 BGB", url: "https://www.gesetze-im-internet.de/bgb/__362.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["schuldrecht-at", "erlöschen", "erfuellung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-schuld-041",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "Schuldrecht AT - Treu und Glauben",
    title: "Leistungspflicht nach Treu und Glauben (§ 242 BGB)",
    prompt:
      "Der Schuldner ist nach § 242 BGB verpflichtet, die Leistung so zu bewirken, wie ___ mit Rücksicht auf die Verkehrssitte es erfordern.",
    options: [
      "die gesetzlichen Vorschriften",
      "Treu und Glauben",
      "die Parteivereinbarung",
      "die wirtschaftliche Vernunft",
    ],
    answers: [1],
    explanation:
      "§ 242 BGB lautet wörtlich: 'Der Schuldner ist verpflichtet, die Leistung so zu bewirken, wie Treu und Glauben mit Rücksicht auf die Verkehrssitte es erfordern.' Der Begriff 'Treu und Glauben' (lat.: bona fides) ist der zentrale Maßstab. § 242 BGB hat breite Wirkung: Er begründet Nebenpflichten, schränkt missbräuchliche Rechtsausübung ein (venire contra factum proprium, Verwirkung, unzulässige Rechtsausübung) und dient als Lückenfüller für Vertragsgestaltung. Die 'Verkehrssitte' ergänzt den Maßstab um branchenübliche Gepflogenheiten.",
    sourceIds: ["bgb242"],
    sources: [
      { id: "bgb242", label: "§ 242 BGB", url: "https://www.gesetze-im-internet.de/bgb/__242.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["schuldrecht-at", "treu-und-glauben"],
    reviewStatus: "beta-ready",
  },
];
